import React from "react";
import { useState } from "react";
import { FiMail, FiAlertCircle, FiCheckCircle, FiInfo, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";

const GAMES = require("../data/games.json");

function Unsubscribe() {
    const [email, setEmail] = useState("");
    const [userId, setUserId] = useState("");
    const [selectedGames, setSelectedGames] = useState([]);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [consent, setConsent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGameToggle = (gameId) => {
        setSelectedGames(prev =>
            prev.includes(gameId)
                ? prev.filter(id => id !== gameId)
                : [...prev, gameId]
        );
    };

    const handleSelectAll = () => {
        const allGameIds = Object.values(GAMES).map(game => game.id);
        const allSelected = allGameIds.length > 0 && allGameIds.every(id => selectedGames.includes(id));
        setSelectedGames(allSelected ? [] : allGameIds);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!email && !userId) {
            setError("Please provide either an email address or user ID.");
            return;
        }

        if (!consent) {
            setError("Please confirm your consent to unsubscribe.");
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {};
            if (email) payload.email = email;
            if (userId) payload.userId = userId;
            if (selectedGames.length > 0) payload.games = selectedGames;

            const response = await fetch('/api/unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setDone(true);
            } else {
                setError("Failed to process unsubscribe request. Please try again or contact us directly.");
            }
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setError("Failed to process unsubscribe request. Please try again or contact us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen pt-20 pb-24">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#6366f1] via-[#8b5cf6] via-[#a855f7] to-[#ec4899] overflow-hidden rounded-b-3xl border-b-4 border-[#fbbf24]/60 shadow-2xl shadow-[#8b5cf6]/50">
                {/* Cosmic nebula overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/20 via-transparent to-[#f472b6]/20"></div>
                <div className="absolute inset-0 bg-gradient-to-bl from-[#fbbf24]/10 via-transparent to-[#06b6d4]/10"></div>
                
                {/* Space decorations */}
                <div className="absolute top-4 left-4 text-4xl animate-bounce-joy">🚮</div>
                <div className="absolute top-8 right-8 text-3xl animate-float">📧</div>
                <div className="absolute bottom-4 left-8 text-3xl animate-wiggle">🔒</div>
                <div className="absolute bottom-8 right-4 text-4xl animate-bounce-joy" style={{ animationDelay: '1s' }}>✨</div>
                <div className="absolute top-1/2 left-1/4 text-2xl animate-twinkle">💫</div>
                <div className="absolute top-1/3 right-1/4 text-2xl animate-float" style={{ animationDelay: '0.5s' }}>🗑️</div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 text-center">
                    <div className="inline-block p-4 bg-white/20 rounded-full mb-6 backdrop-blur-sm">
                        <FiMail className="text-white text-5xl drop-shadow-lg" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg animate-bounce-joy bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] bg-clip-text text-transparent">
                        Account & Data Removal Notice
                    </h1>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#fbbf24] via-[#ec4899] to-[#3b82f6] mb-4 mx-auto rounded-full shadow-lg shadow-[#8b5cf6]/60"></div>
                    <p className="text-white/95 text-lg">Unsubscribe from our mailing list or remove your account</p>
                </div>
            </div>

            <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                {!done ? (
                    <>
                        {/* Warning Notice */}
                        <section className="mb-8">
                            <div className="bg-gradient-to-br from-[#fbbf24]/30 via-[#f59e0b]/20 to-[#d97706]/30 backdrop-blur-sm rounded-2xl p-6 border-2 border-[#fbbf24]/40 shadow-xl">
                                <div className="flex items-start gap-3">
                                    <FiAlertCircle className="text-white text-2xl flex-shrink-0 mt-0.5" />
                                    <div className="text-white text-sm">
                                        <p className="font-semibold mb-2 text-white text-base">Processing Your Deletion Request:</p>
                                        <p className="text-white/90 mb-3">Your submission immediately initiates the queueing of your account for permanent removal.</p>
                                        <p className="text-white/90 mb-2">
                                            Crucial Information: <span className="font-bold text-white">The 10-Day Window</span>
                                        </p>
                                        <ul className="list-disc list-inside space-y-1 ml-2">
                                            <li className="text-white/90">We maintain a mandatory 10-day retention window following this submission.</li>
                                            <li className="text-white/90"><span className="font-bold text-white">This period</span> allows you the option to revoke the deletion request if you change your mind, thereby preserving your account and data.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Form Section */}
                        <section className="mb-8">
                            <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Games Selection */}
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <label className="block text-sm font-medium text-white">
                                                Select Games (Optional)
                                            </label>
                                            <button
                                                type="button"
                                                onClick={handleSelectAll}
                                                className="text-xs px-3 py-1 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-white font-medium"
                                            >
                                                {selectedGames.length === Object.values(GAMES).length ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>
                                        <div className="bg-white/10 border border-white/30 rounded-lg p-4 max-h-60 overflow-y-auto">
                                            <div className="space-y-2">
                                                {Object.values(GAMES).map((game) => (
                                                    <label
                                                        key={game.id}
                                                        className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-white/10 transition"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedGames.includes(game.id)}
                                                            onChange={() => handleGameToggle(game.id)}
                                                            className="w-5 h-5 rounded border-white/40 bg-white/20 text-red-600 focus:ring-2 focus:ring-red-500/20 focus:ring-offset-0"
                                                        />
                                                        <span className="text-white text-sm font-medium">{game.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="mt-2 text-xs text-white/70">
                                            {selectedGames.length === 0
                                                ? "Leave unselected to unsubscribe from all games, or select specific games to unsubscribe only from those."
                                                : `Selected ${selectedGames.length} game(s): ${selectedGames.map(id => Object.values(GAMES).find(g => g.id === id)?.name).join(", ")}`
                                            }
                                        </p>
                                    </div>

                                    {/* User ID Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-white mb-2">
                                            User ID <span className="text-red-300">*</span>
                                        </label>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
                                            <input
                                                type="text"
                                                value={userId}
                                                onChange={(e) => setUserId(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 transition text-white placeholder-white/50"
                                                placeholder="Enter your user ID"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-white/70">(Email or User ID required)</p>
                                    </div>

                                    {/* Email Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-white mb-2">
                                            Email Address <span className="text-red-300">*</span>
                                        </label>
                                        <div className="relative">
                                            <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/20 border border-white/40 focus:border-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 transition text-white placeholder-white/50"
                                                placeholder="your.email@example.com"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-white/70">(Email or User ID required)</p>
                                    </div>

                                    {/* Consent Checkbox */}
                                    <div className="bg-gradient-to-br from-red-900/30 via-red-800/20 to-red-900/30 border-2 border-red-500/40 rounded-lg p-4">
                                        <p className="font-semibold mb-2 text-white">Affirmation of Data Deletion:</p>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={consent}
                                                onChange={(e) => {
                                                    setConsent(e.target.checked);
                                                    setError("");
                                                }}
                                                className="mt-1 w-5 h-5 rounded border-white/40 bg-white/20 text-red-600 focus:ring-2 focus:ring-red-500/20 focus:ring-offset-0 focus:ring-offset-transparent"
                                            />
                                            <div className="flex-1">
                                                <p className="text-white font-semibold mb-2">
                                                    I agree and unequivocally consent to the total and irreversible erasure of all my stored data from the Joyloom platform.
                                                </p>
                                                <p className="text-white/90 text-sm leading-relaxed">
                                                    By completing this form, you affirm your full and final consent for JoyLoom to permanently
                                                    and completely delete all associated data from its systems after the 10-day retention period has concluded.
                                                </p>
                                                <ul className="list-disc list-inside space-y-1 ml-2 leading-relaxed mt-2">
                                                    <li className="text-white/90">
                                                        <span className="font-bold text-white">This action is immediate and irreversible.</span> You can resubscribe at any time through our <Link to="/contact" className="text-white hover:text-white/80 underline">contact page</Link>.
                                                    </li>
                                                </ul>
                                            </div>
                                        </label>
                                    </div>

                                    {/* Data Retention Notice */}
                                    <div className="bg-white/20 border border-white/40 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <FiInfo className="text-white text-xl flex-shrink-0 mt-0.5" />
                                            <div className="text-white/90 text-sm">
                                                <p className="font-semibold mb-1 text-white">Key Information: The 10-Day Retention Window</p>
                                                <p className="text-white/90 leading-relaxed">
                                                    Please note that a <span className="font-semibold text-white">10-day data retention period</span> is in effect starting now.
                                                </p>
                                                <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                                                    <li className="text-white/90"><span className="font-bold text-white">During this period</span>, you retain the option to cancel your deletion request within your account settings.</li>
                                                    <li className="text-white/90">If you cancel, the deletion process will be halted, and your account will remain active.</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Error Message */}
                                    {error && (
                                        <div className="p-4 bg-red-700/30 border-2 border-red-500/50 rounded-lg text-white text-sm flex items-start gap-3">
                                            <FiAlertCircle className="text-red-300 flex-shrink-0 mt-0.5" />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={!consent || isSubmitting || (!email && !userId)}
                                        className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-red-800 disabled:to-red-900 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg transition font-semibold flex items-center justify-center gap-2 text-white shadow-lg shadow-red-600/30"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="animate-spin">⏳</span>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <FiMail />
                                                Unsubscribe
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </section>

                        {/* Alternative Actions */}
                        <section>
                            <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl text-center">
                                <p className="text-white/90 text-sm mb-3">
                                    Changed your mind or need help?
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Link
                                        to="/contact"
                                        className="px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-sm text-white font-medium"
                                    >
                                        Contact Support
                                    </Link>
                                    <Link
                                        to="/"
                                        className="px-4 py-2 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition text-sm text-white font-medium"
                                    >
                                        Back to Home
                                    </Link>
                                </div>
                            </div>
                        </section>
                    </>
                ) : (
                    /* Success State */
                    <div className="bg-gradient-to-br from-green-600/30 via-green-500/20 to-green-600/30 backdrop-blur-sm rounded-2xl p-8 md:p-12 border-2 border-green-400/40 shadow-xl text-center">
                        <div className="inline-block p-4 bg-green-700/20 border-2 border-green-500/50 rounded-full mb-6">
                            <FiCheckCircle className="w-12 h-12 text-green-300" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 text-white">
                            Unsubscribed Successfully
                        </h2>
                        <p className="text-white/90 text-lg mb-2">
                            {email && <span className="font-semibold text-white">{email}</span>}
                            {email && userId && <span className="text-white/80"> and </span>}
                            {userId && <span className="font-semibold text-white">User ID: {userId}</span>}
                            {!email && !userId && <span className="font-semibold text-white">Your account</span>} has been removed from our mailing list.
                        </p>
                        {selectedGames.length > 0 && (
                            <p className="text-white/80 text-sm mb-2">
                                Unsubscribed from: <span className="font-semibold text-white">
                                    {selectedGames.map(id => Object.values(GAMES).find(g => g.id === id)?.name).join(", ")}
                                </span>
                            </p>
                        )}
                        {selectedGames.length === 0 && (
                            <p className="text-white/80 text-sm mb-2">
                                You will no longer receive marketing emails from all JoyLoom Games.
                            </p>
                        )}
                        <p className="text-white/80 text-sm mb-8">
                            {selectedGames.length > 0
                                ? "You will continue to receive emails from other games you're subscribed to."
                                : "You will no longer receive marketing emails from JoyLoom Games."
                            }
                        </p>

                        {/* Important Info */}
                        <div className="bg-white/20 border border-white/40 rounded-lg p-4 mb-6 text-left">
                            <div className="flex items-start gap-3">
                                <FiInfo className="text-white text-xl flex-shrink-0 mt-0.5" />
                                <div className="text-white/90 text-sm">
                                    <p className="font-semibold mb-1 text-white">What happens next?</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2">
                                        <li className="text-white/80">Your email has been removed from our marketing list immediately</li>
                                        <li className="text-white/80">You may still receive transactional emails related to your account</li>
                                        <li className="text-white/80">You can resubscribe anytime by contacting us</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link
                                to="/"
                                className="px-6 py-3 bg-white hover:bg-[#fef3c7] rounded-lg transition font-semibold text-[#6366f1] shadow-lg shadow-white/30"
                            >
                                Return to Home
                            </Link>
                            <Link
                                to="/contact"
                                className="px-6 py-3 bg-white/20 hover:bg-white/30 border border-white/40 rounded-lg transition font-semibold text-white"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default Unsubscribe;
